import type { ImageProps } from '@hover/blueprint';
import { Body, Box, Heading } from '@hover/blueprint';
import { iconExternalLink } from '@hover/icons';
import React, { useEffect, useState } from 'react';

import { Button } from 'src/shared/components';
import type { OptionalTrackingProperties } from 'src/shared/hooks/useTracking';
import { linkPressed } from 'src/shared/telemetry';

import { useHomeownerAppContext } from '../../hooks/useHomeownerAppContext';
import { normalizeHexColorFromSrc } from '../Projects/Project/helpers';

import { BasicCard } from './BasicCard';

export interface LinkoutCardProps {
  buttonHref: string;
  defaultBackgroundColor?: string;
  useColorSampling?: boolean;
  buttonText: string;
  title: string;
  description: string;
  optionalTrackingProperties: OptionalTrackingProperties;
  imageProps?: Partial<ImageProps>;
  imageSrc?: string;
  tagline?: string;
}

export const LinkoutCard = ({
  defaultBackgroundColor,
  useColorSampling = false,
  buttonText,
  buttonHref,
  description,
  imageProps,
  imageSrc,
  title,
  optionalTrackingProperties,
  tagline,
}: LinkoutCardProps) => {
  const { trackingData } = useHomeownerAppContext();
  const [backgroundColor, setBackgroundColor] = useState(
    defaultBackgroundColor,
  );

  useEffect(() => {
    if (imageSrc && useColorSampling) {
      void normalizeHexColorFromSrc(imageSrc).then(color =>
        setBackgroundColor(color),
      );
    }
  }, [imageSrc, useColorSampling]);

  return (
    <BasicCard backgroundColorProp={backgroundColor} textAlign="center">
      {/* This aspect ratio is variable if the images are for the Pella cards vs default cards
      It is also non-standard for our design system and should be reconciled longer term */}
      <Box
        aspectRatio="2.15"
        backgroundImage={imageSrc}
        backgroundPosition="top center"
        backgroundRepeat="no-repeat"
        backgroundSize="contain"
        marginBottom={500}
        marginTop={700}
        {...imageProps}
      />
      <Heading
        as="span"
        color="inherit"
        mx={700}
        my={0}
        size={600}
        sx={{ textWrap: 'balance' }}
      >
        {title}
      </Heading>
      <Body
        color="inherit"
        flexGrow="1"
        mx={700}
        my={300}
        size={300}
        sx={{ textWrap: 'balance' }}
      >
        {description}
      </Body>
      <Button
        as="a"
        href={buttonHref}
        iconAfter={iconExternalLink}
        includeTrackingImpression
        margin="auto"
        mt={600}
        optionalTrackingProperties={{
          destination_url: buttonHref,
          ...optionalTrackingProperties,
          ...trackingData,
        }}
        size="large"
        target="_blank"
        textDecoration="none"
        track={linkPressed}
        width="min-content"
      >
        {buttonText}
      </Button>
      <Body
        color="text.secondaryInvert"
        margin={0}
        mb={500}
        mt={900}
        size={200}
      >
        {tagline}
      </Body>
    </BasicCard>
  );
};
